import { ProductTileProps } from '@pb/components'
import {
  getUserCustomerGroup,
  isWeekdayBrand,
  isHmBrand,
  getDiscount,
  getLabelFromStorage,
  isAkamaiEnabled
} from '@pb/utils'

import {
  MyClosetDetailPrepData,
  ImageMyClosetDetail,
} from '../components/Stylehub/components/MyCloset/types'
import {
  NormalizedFavourites,
  FavouritesResponse,
  FavouritesEntries,
  ProductSizes,
  EnergyLabel,
  ProductDetailContainerPadding,
  MiniCartData,
  HiddenFavouritesData,
  Variant,
} from './types'

const showColorDetail = process.env.IS_PRODUCTDETAIL_COLOR_ENABLED === 'enabled'
const showPriceAboveProductName =
  process.env.IS_PRODUCTDETAIL_PRICE_ABOVE_PRODUCTNAME_ENABLED === 'enabled'
const showLargeContainerPadding =
  process.env.IS_PRODUCTDETAIL_LARGE_CONTAINER_PADDING_ENABLED === 'enabled'
const enabledStoreLocator = process.env.IS_PRODUCTDETAIL_STORE_LOCATOR_ENABLED === 'enabled'
const shouldUseAkamai = isAkamaiEnabled()

const getItemSizesAvailable = (data: FavouritesEntries): ProductSizes[] =>
  data.variants.map(({ size }) => {
    const sizeAvailableObject = { sizeCode: size.sizeCode, sizeName: size.name }
    return sizeAvailableObject
  })

const getEnergyLabelProps = (data: FavouritesEntries): EnergyLabel => {
  const energyClass =
    data?.energyClass === ('A++' || 'A+' || 'A' || 'B' || 'C' || 'D') ? data.energyClass : undefined

  const energyLetter =
    data?.energyClass?.charAt(0) === ('A' || 'B' || 'C' || 'D')
      ? (data.energyClass.charAt(0) as 'A' | 'B' | 'C' | 'D')
      : undefined

  const energySubSignCheck = data?.energyClass?.slice(1) === '++' || '+'
  const energySupSign = energySubSignCheck ? (data?.energyClass?.slice(1) as '++' | '+') : undefined
  return {
    energyClass,
    energyLetter,
    energySupSign,
    energyClassCode: data.energyClassCode || (energyClass && 'app') || undefined,
    energyClassIntervalCode: data.energyClassIntervalCode,
    energyClassInterval: data.energyClassInterval,
    articleId: data.articleCode,
    arrowDirection: 'right',
  }
}

const getVariantOutOfStock = (item: FavouritesEntries): boolean => {
  const findSelectedVariant = item.variants.find((variant) => variant.selected === true)
  const selectedVariantOutOfStock = findSelectedVariant?.stock?.stockLevel === 0

  return !item.inStock || selectedVariantOutOfStock
}

const getCorrectSizedImage = (url: string): string | null => {
  if (!url) {
    return null
  }

  const isHMimage = url.includes('hm.com')
  if (!isHmBrand && !isHMimage) {
    // Adjustments to correct image size for PB
    const injectStr = (str: string, index: number, stringToAdd: string): string => {
      return str.substring(0, index) + stringToAdd + str.substring(index, str.length)
    }

    const decodeImage = decodeURIComponent(url)
    if (decodeImage.endsWith('[file:/product/cart]')) {
      const subString = decodeImage.split('?')[0]
      const missingSettings =
        'set=key[resolve.width],value[500]&set=key[resolve.height],value[10000]&set=key[resolve.allowImageUpscaling],value[0]&set=key[resolve.format],value[jpeg]&'
      const updateMissingSettings = injectStr(decodeImage, subString.length + 1, missingSettings)
      const updateFile = updateMissingSettings.replace(
        '[file:/product/cart]',
        '[file:/product/dynamic.chain]',
      )
      const encodedImage = encodeURIComponent(updateFile)

      return encodedImage || url
    }
    return url
  }

  // Adjustments to correct image size for H&M
  const decodeImage = decodeURIComponent(url)
  const updateFile = decodeImage.replace('[file:/product/main]', '[file:/product/cart]')
  const updateRes = updateFile.replace('res[z]', 'res[m]')
  const encodedImageMiniature = encodeURIComponent(updateRes)

  return encodedImageMiniature || url
}

const getImageType = (imageUrl: string): string => {
  const regexp = /type\[(.*?)\]/g
  const matches = regexp.exec(imageUrl)
  return matches !== null ? matches[1] : ''
}

const getHasModelImage = (imageUrl: string, normalPictureUrl: string): boolean => {
  const decodedImage = decodeURI(imageUrl)
  const decodedNormalPicture = decodeURI(normalPictureUrl)
  const imageType = getImageType(decodedImage)
  const normalPictureType = getImageType(decodedNormalPicture)
  const hasDuplicateImageTypes = imageType === normalPictureType

  if (hasDuplicateImageTypes) {
    return false
  }
  return true
}

const getModelImageUrl = (item: FavouritesEntries): string => {
  let getImageUrl: string
  if(shouldUseAkamai) {
    getImageUrl = item.normalPicture.baseUrl || item.normalPicture.url
  }
  else if (item.normalPicture.url) {
    getImageUrl = getCorrectSizedImage(item.normalPicture.url) || item.normalPicture.url
    return getImageUrl
  }
  return ''
}

const getProductImageUrl = (item: FavouritesEntries): string => {
  let getImageUrl: string
  if (!isHmBrand) {
    if(shouldUseAkamai) {
      getImageUrl = item.image.baseUrl || item.image.url
    } else {
      getImageUrl = getCorrectSizedImage(item.image.url) || item.image.url
    }
  } else {
    getImageUrl = item.image.url
  }

  return getImageUrl
}

export const normalizeFavouritesResponse = (
  favouritesResponse: FavouritesResponse,
  isClubMarket: boolean,
): ProductTileProps[] => {
  const productAltLabelAppendix = getLabelFromStorage('order.product')
  const adjustedFavourites = favouritesResponse.data.entries.map(
    (currentValue: FavouritesEntries) => {
      const redPrice =
        currentValue.discountedPrice &&
          (currentValue.discountedPrice?.type === 'RED' ||
            (!!currentValue.discountedPrice?.type && isClubMarket))
          ? currentValue.discountedPrice?.formattedValue
          : undefined

      const redPriceValue =
        currentValue.discountedPrice?.type === 'RED' ||
          (!!currentValue.discountedPrice?.type && isClubMarket)
          ? currentValue.discountedPrice?.value
          : undefined

      const yellowPrice =
        currentValue.isYellowPriceAvailable && currentValue.discountedPrice?.type === 'YELLOW'
          ? currentValue.discountedPrice?.formattedValue
          : undefined

      const yellowPriceValue =
        currentValue.isYellowPriceAvailable && currentValue.discountedPrice?.type === 'YELLOW'
          ? currentValue.discountedPrice?.value
          : undefined

      const hasModelImage = getHasModelImage(currentValue.image.url, currentValue.normalPicture.url)

      return {
        imageId: currentValue.image.id,
        primaryImageId: currentValue.image.id,
        imageProductSrc: getProductImageUrl(currentValue) || '',
        imageProductAlt: `${currentValue.name} ${productAltLabelAppendix}` || '',
        imageModelSrc: hasModelImage ? getModelImageUrl(currentValue) : '',
        imageModelAlt: hasModelImage ? currentValue.name : '',
        articleCode: currentValue.articleCode,
        variantCode: currentValue.code,
        code: currentValue.code,
        baseProductCode: currentValue.baseProductCode,
        category: currentValue.categoryCode,
        pdpUrl: currentValue.productUrl,
        title: currentValue.name,
        showEnergyButton: !!currentValue.energyClass,
        legalText: currentValue.favoriteNonProductMarker?.legalText,
        promotionalMarkerText: currentValue.favoriteNonProductMarker?.text,
        marketingMarkerType: currentValue.favoriteProductMarker?.type,
        marketingMarkerText: currentValue.favoriteProductMarker?.text,
        currency: currentValue.ordinaryPrice?.currencyIso,
        regularPrice: currentValue.ordinaryPrice?.formattedValue,
        regularPriceValue: currentValue.ordinaryPrice?.value,
        redPrice,
        redPriceValue,
        yellowPrice,
        yellowPriceValue,
        priorPrice: currentValue.priorPrice?.formattedValue,
        comparativePrice: currentValue.comparativePrice && {
          formattedPrice: currentValue.comparativePrice?.formattedPrice,
          comparativeQuantity: currentValue.comparativePrice?.comparativeQuantity,
          quantity: currentValue?.quantity || currentValue.comparativePrice.quantity
        },
        showOutOfStockImageOverlay: getVariantOutOfStock(currentValue),
        isNewProduct: !!currentValue?.sellingAttribute,
        newProductLabel: currentValue.sellingAttribute,
        showFavoriteButton: false,
        showRemoveButton: true,
        showDataList: true,
        discountPercentage:
          isHmBrand && !currentValue.priorPrice
            ? getDiscount(currentValue.ordinaryPrice?.value, redPriceValue)
            : undefined,
        inStock: currentValue.inStock,
        inStoreStock: currentValue?.inStoreStock,
        productColor: showColorDetail
          ? {
            hexColor: currentValue?.color?.code,
            colorLink: '#',
            colorName: currentValue?.color?.text,
          }
          : undefined,
        productVideo: currentValue.onSiteAttributes?.productVideo,
        sizePickerData: { item: currentValue },
        energyLabelProps: getEnergyLabelProps(currentValue),
        storeSearchData: {
          allProductSizes: getItemSizesAvailable(currentValue),
        },
        preAccessGroups: currentValue.preAccessGroups,
        preAccessEndDate: currentValue.preAccessEndDate,
        preAccessStartDate: currentValue.preAccessStartDate,
        userCustomerGroup: getUserCustomerGroup(),
        brandName: currentValue.brandsData?.brandName,
        isExternalBrand: currentValue.brandsData?.external,
        isPriceAboveProductName: showPriceAboveProductName,
        isStoreLocatorEnabled: enabledStoreLocator,
        productDetailContainerPadding: showLargeContainerPadding
          ? ProductDetailContainerPadding.largeContainerPadding
          : ProductDetailContainerPadding.smallContainerPadding,
        isChangeImageOnHoverActive: isHmBrand,
      }
    },
  )

  return adjustedFavourites as unknown as ProductTileProps[]
}

const getSizeName = (variants: Variant[]): string | undefined => {
  if (!variants) {
    return undefined
  }

  const getVariant = variants.find((variant) => variant.selected === true)

  return getVariant ? getVariant.size.name : undefined
}

export const getMiniCartData = (data: NormalizedFavourites): MiniCartData => {
  let decodeImageMiniature

  if (data.useModelAsDefault && data.imageModelSrc) {
    decodeImageMiniature = decodeURIComponent(data.imageModelSrc)
  } else if (data.imageProductSrc) {
    decodeImageMiniature = decodeURIComponent(data.imageProductSrc)
  } else {
    decodeImageMiniature = ''
  }
  const updateFile = decodeImageMiniature.replace(
    '[file:/product/cart]',
    '[file:/product/miniature]',
  )
  const updateRes = updateFile.replace('res[z]', 'res[l]')
  return {
    productUrl: data.pdpUrl,
    imgUrl: isWeekdayBrand ? decodeImageMiniature : updateRes,
    imgAlt: data.title,
    itemName: data.title,
    itemVariantCode: data.variantCode,
    itemSize: getSizeName(data.sizePickerData?.item?.variants as Variant[]),
    itemOrdinaryPrice: data.regularPrice,
    itemOrdinaryPriceValue: data.regularPriceValue,
    itemRedPrice: data.redPrice,
    itemRedPriceValue: data.redPriceValue,
    itemPriorPrice: data.priorPrice,
    itemYellowPrice: data.yellowPrice,
    itemYellowPriceValue: data.yellowPriceValue,
    itemColor: data.productColor?.colorName,
    fewPieceLeft: data.sizePickerData?.item.fewPieceLeft,
  }
}

export const normalizeHiddenFavouritesResponse = (
  favouritesResponse: FavouritesResponse,
): HiddenFavouritesData[] => {
  const hiddenFavouritesData = favouritesResponse.data.entries.map(
    (currentValue: FavouritesEntries) => {
      return {
        code: currentValue.code,
        title: currentValue.name,
        removed: false,
      }
    },
  )

  return hiddenFavouritesData
}

// TODO: Adjust types for below: myclosetPrepData
export const myclosetPrepData = (myclosetResult, isMyClosetDetailEnabled): any =>
  [myclosetResult].flat().map((result, i) =>
    result.mainCategories.map((subcategory) =>
      subcategory.subCategories.map((articles) =>
        articles.articles.map((article) => ({
          imageModelSrc: article.modelImage.url,
          imageModelAlt: article.modelImage.type,
          imageProductSrc: article.productImage.url,
          imageProductAlt: article.productImage.type,
          articleCode: article.code,
          category: result.mainCategories[i].subCategories[i].name,
          mainCategory: result.mainCategories[i].name,
          pdpUrl: !isMyClosetDetailEnabled && article.articleUrl,
          title: article.name,
          hasPriceMarker: article.hasPriceMarker,
          regularPrice: article.whitePrice.formattedPrice,
          redPrice: article.whitePrice.formattedPrice,
          bluePrice: article.whitePrice.formattedPrice,
          yellowPrice: article.whitePrice.formattedPrice,
          isOutOfStock: !article.inStock,
          isNewProduct: article.new,
          brandName: article.brandName,
          isExternalBrand: article.external,
          marketingMarkerType: '',
          marketingMarkerText: '',
          availableSizeOptions: article.availableSizeOptions,
          availableSizeOptionCodes: article.availableSizeOptionCodes,
        })),
      ),
    ),
  )

const myClosetImgData = (images): ImageMyClosetDetail[] =>
  images.map((img) => {
    return {
      url: img.url,
      mobileUrl: img.url,
      altText: img.type,
    }
  })

const myClosetDescriptionData = (materials): void =>
  materials.map((element) => element.name).join('/')

export const myclosetDetailPrepData = (myclosetDetailResult): MyClosetDetailPrepData =>
  [myclosetDetailResult].flat().map((result) => ({
    code: result.code,
    name: result.name,
    images: myClosetImgData(result.images),
    descriptionText: [result.colour, myClosetDescriptionData(result.materials), result.size].filter(
      (value) => value,
    ),
    careForit: result.careInstructions,
  }))
